import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, CardActionArea, Chip } from '@mui/material';
import { 
  Calculate, 
  Assessment, 
  Business, 
  School, 
  AutoAwesome, 
  Insights,
  AccountBalance,
  Description
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface ResourceLink {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  tags: string[];
  popular?: boolean;
}

const ResourceHub: React.FC = () => {
  const navigate = useNavigate();

  const resources: ResourceLink[] = [
    {
      title: 'Sales Tax Calculator',
      description: 'Calculate accurate sales tax rates for any US address. Free tool with zip code precision.',
      icon: <Calculate sx={{ fontSize: 40, color: 'primary.main' }} />,
      path: '/tax/calculator',
      tags: ['Tool', 'Free'],
      popular: true
    },
    {
      title: 'Nexus Threshold Checker',
      description: 'Monitor your sales across all states and get alerts when approaching nexus thresholds.',
      icon: <Assessment sx={{ fontSize: 40, color: 'secondary.main' }} />,
      path: '/tax/nexus-threshold',
      tags: ['Tool', 'Monitoring'],
      popular: true
    },
    {
      title: 'AI Tax Advisor',
      description: 'Get instant answers to sales tax questions using AI trained on official state tax codes.',
      icon: <AutoAwesome sx={{ fontSize: 40, color: 'warning.main' }} />,
      path: '/advisor',
      tags: ['AI', 'Free'],
      popular: true
    },
    {
      title: 'Tax Compliance Guide 2025',
      description: 'Complete guide to sales tax compliance including economic nexus, thresholds, and automation.',
      icon: <School sx={{ fontSize: 40, color: 'success.main' }} />,
      path: '/blog/sales-tax-compliance-guide-2025',
      tags: ['Guide', 'Education']
    },
    {
      title: 'State Nexus Thresholds',
      description: 'Reference guide for economic nexus thresholds in all 50 US states. Updated for 2025.',
      icon: <AccountBalance sx={{ fontSize: 40, color: 'info.main' }} />,
      path: '/resources/state-nexus-thresholds',
      tags: ['Reference', 'Guide']
    },
    {
      title: 'Tax Registration Manager',
      description: 'Track and manage sales tax registrations across multiple states with deadline reminders.',
      icon: <Business sx={{ fontSize: 40, color: 'primary.main' }} />,
      path: '/tax/registration',
      tags: ['Tool', 'Management']
    },
    {
      title: 'Transaction Insights',
      description: 'Analyze transaction patterns, identify tax variances, and optimize compliance processes.',
      icon: <Insights sx={{ fontSize: 40, color: 'secondary.main' }} />,
      path: '/dashboard',
      tags: ['Analytics', 'Dashboard']
    },
    {
      title: 'Blog & Knowledge Base',
      description: 'Articles, guides, and insights on sales tax compliance, nexus laws, and industry trends.',
      icon: <Description sx={{ fontSize: 40, color: 'success.main' }} />,
      path: '/resources/blogs-knowledge',
      tags: ['Blog', 'Resources']
    }
  ];

  return (
    <>
      <Helmet>
        <title>Sales Tax Resources & Tools | GoTruTax Resource Hub</title>
        <meta name="description" content="Free sales tax calculators, nexus threshold checkers, AI tax advisor, compliance guides, and educational resources. Everything you need for multi-state tax compliance." />
        <meta name="keywords" content="sales tax calculator, nexus threshold checker, tax compliance guide, tax resources, sales tax tools, tax advisor, compliance resources" />
        <link rel="canonical" href="https://gotrutax.com/resources" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Sales Tax Resources & Tools",
            "description": "Comprehensive collection of sales tax compliance tools, calculators, guides, and educational resources.",
            "url": "https://gotrutax.com/resources",
            "publisher": {
              "@type": "Organization",
              "name": "GoTruTax"
            }
          })}
        </script>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h1" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 2 }}>
            Sales Tax Resources & Tools
          </Typography>
          <Typography variant="h2" sx={{ fontSize: '1.25rem', fontWeight: 400, color: 'text.secondary', mb: 4 }}>
            Free tools, calculators, guides, and resources to simplify your sales tax compliance
          </Typography>
        </Box>

        {/* Popular Resources */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h3" sx={{ fontSize: '1.75rem', fontWeight: 700, mb: 3 }}>
            Most Popular
          </Typography>
          <Grid container spacing={3}>
            {resources.filter(r => r.popular).map((resource) => (
              <Grid item xs={12} md={4} key={resource.path}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardActionArea onClick={() => navigate(resource.path)} sx={{ height: '100%', p: 3 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ mb: 2 }}>
                        {resource.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {resource.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {resource.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {resource.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" color={tag === 'Free' ? 'success' : 'default'} />
                        ))}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* All Resources */}
        <Box>
          <Typography variant="h3" sx={{ fontSize: '1.75rem', fontWeight: 700, mb: 3 }}>
            All Resources
          </Typography>
          <Grid container spacing={3}>
            {resources.map((resource) => (
              <Grid item xs={12} sm={6} md={4} key={resource.path}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardActionArea onClick={() => navigate(resource.path)} sx={{ height: '100%', p: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        {resource.icon}
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                            {resource.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {resource.tags.map(tag => (
                              <Chip 
                                key={tag} 
                                label={tag} 
                                size="small" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                                color={tag === 'Free' ? 'success' : tag === 'Popular' ? 'primary' : 'default'} 
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {resource.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* SEO Internal Links Section */}
        <Box sx={{ mt: 8, p: 4, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 3 }}>
            Learn More About Sales Tax Compliance
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Tax Compliance Topics</Typography>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li><a href="/blog/sales-tax-compliance-guide-2025">What is Economic Nexus?</a></li>
                <li><a href="/resources/state-nexus-thresholds">Economic Nexus Thresholds by State</a></li>
                <li><a href="/resources/blogs-knowledge#marketplace-facilitator">Marketplace Facilitator Laws</a></li>
                <li><a href="/resources/blogs-knowledge#remote-seller">Remote Seller Obligations</a></li>
              </ul>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Popular Tools</Typography>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li><a href="/tax/calculator">Free Sales Tax Calculator</a></li>
                <li><a href="/tax/nexus-threshold">Nexus Threshold Monitor</a></li>
                <li><a href="/advisor">AI Tax Advisor (Free)</a></li>
                <li><a href="/get-started">Start Free Account</a></li>
              </ul>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Integrations</Typography>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li><a href="/integrations/shopify">Shopify Tax Integration</a></li>
                <li><a href="/integrations/quickbooks">QuickBooks Online Integration</a></li>
                <li><a href="/integrations/stripe">Stripe Sales Sync</a></li>
                <li><a href="/dashboard">CSV Transaction Import</a></li>
              </ul>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Company</Typography>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li><a href="/">Home</a></li>
                <li><a href="/contact">Contact Support</a></li>
                <li><a href="/privacy-policy">Privacy Policy</a></li>
                <li><a href="/tos">Terms of Service</a></li>
              </ul>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default ResourceHub;
